import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import SearchIcon from '@mui/icons-material/Search';
import { brand } from 'themes/brand';

export interface Item {
  id: string;
  name: string;
}

interface AsyncSearchableMultiSelectProps {
  selectedIds: string[];
  selectedItems: Item[];
  onChange: (items: Item[]) => void;
  onSearch: (query: string, signal?: AbortSignal) => Promise<Item[]>;
  label?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  singularLabel?: string;
  pluralLabel?: string;
}

const AsyncSearchableMultiSelect: React.FC<AsyncSearchableMultiSelectProps> = ({
  selectedIds,
  selectedItems,
  onChange,
  onSearch,
  label = 'Selecionar',
  searchPlaceholder = 'Buscar...',
  emptyMessage = 'Nenhum item encontrado',
  singularLabel,
  pluralLabel,
}) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<Item[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const open = Boolean(anchorEl);
  const hasSelection = selectedIds.length > 0;
  const isFloating = open || hasSelection;

  const singular = singularLabel ?? label.toLowerCase();
  const plural = pluralLabel ?? `${label.toLowerCase()}s`;

  const triggerText =
    selectedIds.length === 0
      ? ''
      : selectedIds.length === 1
      ? `1 ${singular} selecionado`
      : `${selectedIds.length} ${plural} selecionados`;

  const fetchResults = async (query: string, signal: AbortSignal) => {
    setLoading(true);
    try {
      const data = await onSearch(query, signal);
      if (!signal.aborted) setResults(data);
    } catch {
      // aborted requests are ignored
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  };

  const handleOpen = () => {
    setAnchorEl(triggerRef.current);
  };

  const handleClose = () => {
    abortRef.current?.abort();
    setAnchorEl(null);
    setSearch('');
    setResults([]);
  };

  useEffect(() => {
    if (!open) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      abortRef.current?.abort();
      abortRef.current = new AbortController();
      fetchResults(search, abortRef.current.signal);
    }, 500);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search, open]);

  const handleToggle = (item: Item) => {
    if (selectedIds.includes(item.id)) {
      onChange(selectedItems.filter((s) => s.id !== item.id));
    } else {
      onChange([...selectedItems, item]);
    }
  };

  const handleRemove = (id: string) =>
    onChange(selectedItems.filter((s) => s.id !== id));

  const handleClear = () => onChange([]);

  // Items shown in the list: search results merged with selected items not yet in results
  const listItems = results.some((r) => selectedIds.includes(r.id))
    ? results
    : [
        ...selectedItems.filter((s) => !results.find((r) => r.id === s.id)),
        ...results,
      ];

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        ref={triggerRef}
        onClick={handleOpen}
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          border: open
            ? `2px solid ${brand.primary}`
            : '1px solid rgba(0, 0, 0, 0.23)',
          borderRadius: '4px',
          padding: open ? '15.5px 13px' : '16.5px 14px',
          cursor: 'pointer',
          backgroundColor: '#fff',
          '&:hover': {
            borderColor: open ? brand.primary : 'rgba(0, 0, 0, 0.87)',
          },
        }}
      >
        <Typography
          component="span"
          sx={{
            position: 'absolute',
            left: isFloating ? '8px' : '14px',
            top: isFloating ? '-9px' : '50%',
            transform: isFloating ? 'none' : 'translateY(-50%)',
            fontSize: isFloating ? '12px' : '16px',
            color: open ? brand.primary : 'rgba(0, 0, 0, 0.6)',
            backgroundColor: '#fff',
            padding: '0 4px',
            transition: 'all 0.15s ease',
            pointerEvents: 'none',
            lineHeight: 1,
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Typography>

        <Typography
          sx={{
            flex: 1,
            fontSize: '16px',
            color: hasSelection ? 'text.primary' : 'transparent',
            userSelect: 'none',
          }}
        >
          {triggerText || '​'}
        </Typography>

        <UnfoldMoreIcon
          sx={{ color: 'rgba(0, 0, 0, 0.54)', fontSize: '20px' }}
        />
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          elevation: 3,
          sx: {
            width: anchorEl?.offsetWidth ?? 300,
            maxHeight: 360,
            display: 'flex',
            flexDirection: 'column',
            mt: 0.5,
            borderRadius: '4px',
          },
        }}
      >
        <Box
          sx={{
            px: 1.5,
            py: 1,
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
            variant="standard"
            InputProps={{
              disableUnderline: true,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    fontSize="small"
                    sx={{ color: 'text.disabled' }}
                  />
                </InputAdornment>
              ),
              endAdornment: loading ? (
                <InputAdornment position="end">
                  <CircularProgress size={14} />
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>

        <List sx={{ overflow: 'auto', flex: 1, py: 0.5 }}>
          {!loading && listItems.length === 0 && (
            <Box sx={{ px: 2, py: 1.5, textAlign: 'center' }}>
              <Typography variant="body2" color="text.disabled">
                {emptyMessage}
              </Typography>
            </Box>
          )}
          {listItems.map((item) => {
            const isSelected = selectedIds.includes(item.id);
            return (
              <ListItemButton
                key={item.id}
                onClick={() => handleToggle(item)}
                selected={isSelected}
                sx={{
                  py: 0.75,
                  px: 2,
                  '&.Mui-selected': {
                    backgroundColor: 'transparent',
                    color: 'primary.main',
                  },
                  '&.Mui-selected:hover': {
                    backgroundColor: 'primary.50',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 28 }}>
                  {isSelected && <CheckIcon fontSize="small" color="primary" />}
                </ListItemIcon>
                <ListItemText
                  primary={item.name}
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: isSelected ? 500 : 400,
                    color: isSelected ? 'primary.main' : 'text.primary',
                  }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Popover>

      {hasSelection && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.75,
            mt: 1,
            alignItems: 'center',
          }}
        >
          {selectedItems.map((item) => (
            <Chip
              key={item.id}
              label={item.name}
              onDelete={() => handleRemove(item.id)}
              size="small"
              sx={{
                borderRadius: '16px',
                backgroundColor: '#f0f0f0',
                color: 'text.secondary',
                fontWeight: 400,
                '& .MuiChip-deleteIcon': { color: 'rgba(0,0,0,0.4)' },
              }}
            />
          ))}
          <Button
            size="small"
            onClick={handleClear}
            sx={{
              textTransform: 'none',
              color: 'text.secondary',
              minWidth: 'auto',
              px: 0.5,
            }}
          >
            Limpar
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default AsyncSearchableMultiSelect;
